import Head from "next/head";
import { useMemo, useState } from "react";

function CodeBlock({ code, label = "Code" }) {
    const [copied, setCopied] = useState(false);

    async function copy() {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            // fallback
            const el = document.createElement("textarea");
            el.value = code;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        }
    }

    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="text-sm font-semibold text-gray-800">{label}</div>
                <button
                    type="button"
                    onClick={copy}
                    className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
                >
                    {copied ? "Скопійовано ✅" : "Скопіювати"}
                </button>
            </div>
            <pre className="text-sm leading-6 p-4 overflow-auto bg-white">
        <code>{code}</code>
      </pre>
        </div>
    );
}

function Accordion({ title, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
            >
                <div className="font-semibold text-gray-900">{title}</div>
                <div className="text-gray-400">{open ? "—" : "+"}</div>
            </button>
            {open && <div className="px-5 pb-5 text-gray-700">{children}</div>}
        </div>
    );
}

function Step({ n, title, children, done, onToggle }) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <div className="flex items-start gap-4">
                <button
                    type="button"
                    onClick={onToggle}
                    className={`mt-1 h-6 w-6 rounded-md border flex items-center justify-center ${
                        done ? "bg-green-600 border-green-600 text-white" : "bg-white border-gray-300 text-transparent"
                    }`}
                    aria-label="toggle step"
                >
                    ✓
                </button>
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              Крок {n}
            </span>
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                    </div>
                    <div className="mt-3 text-gray-700 leading-relaxed">{children}</div>
                </div>
            </div>
        </div>
    );
}

export default function HoroshopIntegrationPublicPage() {
    const [done, setDone] = useState({
        s1: false,
        s2: false,
        s3: false,
        s4: false,
        s5: false,
        s6: false,
    });

    const scriptSnippet = useMemo(() => {
        return `<script async src='https://afivio.com/js/afivio.min.js' data-afivio='YOUR_COMPANY_ID'></script>`;
    }, []);

    const authCurlTemplate = useMemo(() => {
        return (
            "curl -X POST 'https://<HOST>/api/auth/' \\\n" +
            "  -H 'Content-Type: application/json' \\\n" +
            "  -d '{\n" +
            '    "login": "<API_LOGIN>",\n' +
            '    "password": "<API_PASSWORD>"\n' +
            "  }'"
        );
    }, []);

    const authCurlExample = useMemo(() => {
        return (
            "curl -X POST 'https://my-shop.com/api/auth/' \\\n" +
            "  -H 'Content-Type: application/json' \\\n" +
            "  -d '{\n" +
            '    "login": "api_user",\n' +
            '    "password": "pass123"\n' +
            "  }'"
        );
    }, []);

    const subscribeCurlExample = useMemo(() => {
        return (
            "curl -X POST 'https://my-shop.com/api/hooks/subscribe/' \\\n" +
            "  -H 'Content-Type: application/json' \\\n" +
            "  -d '{\n" +
            '    "token": "<TOKEN>",\n' +
            '    "event": "order_created",\n' +
            '    "target_url": "https://afivio.com/api/horoshop/webhooks/order_created?webhook_secret=<SECRET>"\n' +
            "  }'"
        );
    }, []);

    return (
        <>
            <Head>
                <title>Afivio × Horoshop — Інструкція інтеграції</title>
                <meta
                    name="description"
                    content="Покрокова інструкція інтеграції Afivio з Horoshop: реєстрація, скрипт у <head>, отримання token та підключення вебхуків."
                />
            </Head>

            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">

                <main className="max-w-5xl mx-auto px-4 py-10">
                    {/* Hero */}
                    <section className="rounded-3xl p-8 sm:p-10 bg-white border border-gray-200 shadow-sm">
                        <div className="flex flex-col gap-6">
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                                    Інтеграція Afivio з Horoshop
                                </h1>
                                <p className="mt-3 text-gray-700 text-lg leading-relaxed">
                                    Ця інструкція зроблена для людей, які <b>вперше</b> чують про Afivio і хочуть швидко підключити
                                    партнерську програму до магазину Horoshop — без програмування та за декілька хвилин.
                                </p>
                            </div>

                            <div className="grid sm:grid-cols-3 gap-3">
                                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                    <div className="font-bold text-gray-900">⏱ 10–20 хв</div>
                                    <div className="text-sm text-gray-700 mt-1">Середній час налаштування</div>
                                </div>
                                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                    <div className="font-bold text-gray-900">🔐 Token не зберігається</div>
                                    <div className="text-sm text-gray-700 mt-1">Використовується лише для підписки</div>
                                </div>
                                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                    <div className="font-bold text-gray-900">🧩 3 вебхуки</div>
                                    <div className="text-sm text-gray-700 mt-1">order_created, order_paid, user_signup</div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* What you'll need */}
                    <section className="mt-10 grid md:grid-cols-2 gap-6">
                        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-7">
                            <h2 className="text-xl font-extrabold text-gray-900">Що вам потрібно</h2>
                            <ul className="mt-4 space-y-2 text-gray-700 leading-relaxed">
                                <li>• Доступ до адмін-панелі Horoshop</li>
                                <li>• Можливість створити API-користувача (login/password)</li>
                                <li>• Будь-який термінал (локальний або онлайн)</li>
                            </ul>
                        </div>

                        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-7">
                            <h2 className="text-xl font-extrabold text-gray-900">Що ви отримаєте</h2>
                            <ul className="mt-4 space-y-2 text-gray-700 leading-relaxed">
                                <li>• Автоматичні комісії / події на основі вебхуків Horoshop</li>
                                <li>• Правильний referral tracking через скрипт у <code>&lt;head&gt;</code></li>
                                <li>• Прозору перевірку: статус підписок і контроль інтеграції</li>
                            </ul>
                        </div>
                    </section>

                    {/* Steps */}
                    <section id="steps" className="mt-10 space-y-5">
                        <div className="flex items-end justify-between flex-wrap gap-3">
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Кроки інтеграції</h2>
                            <div className="text-sm text-gray-600">
                                Порада: ставте галочки — вони збережуться тільки на цій сторінці.
                            </div>
                        </div>

                        <Step
                            n="1"
                            title="Створіть акаунт в Afivio"
                            done={done.s1}
                            onToggle={() => setDone((p) => ({ ...p, s1: !p.s1 }))}
                        >
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>
                                    Перейдіть у{" "}
                                    <a className="text-blue-700 underline" href="/signup">
                                        Afivio (реєстрація)
                                    </a>{" "}
                                    та створіть акаунт.
                                </li>
                                <li>Після входу оберіть інтеграцію “Horoshop”.</li>
                            </ol>
                        </Step>

                        <Step
                            n="2"
                            title="Додайте tracking-скрипт у Horoshop (всередині <head>)"
                            done={done.s2}
                            onToggle={() => setDone((p) => ({ ...p, s2: !p.s2 }))}
                        >
                            <p className="mb-3">
                                У Afivio ви побачите tracking-скрипт із вашим <b>Company ID</b>. Його потрібно вставити в Horoshop:
                                <b> Horoshop → Скрипти → “Скрипти всередині тегу &lt;head&gt;”</b>.
                            </p>

                            <CodeBlock
                                label="Tracking script (приклад)"
                                code={scriptSnippet}
                            />

                            <div className="mt-4 rounded-2xl bg-blue-50 border border-blue-100 p-4 text-sm text-gray-800">
                                <b>Важливо:</b> скрипт має бути доданий <b>один раз</b>. Після вставки збережіть зміни в Horoshop.
                            </div>
                        </Step>

                        <Step
                            n="3"
                            title="Отримайте Horoshop token через auth"
                            done={done.s3}
                            onToggle={() => setDone((p) => ({ ...p, s3: !p.s3 }))}
                        >
                            <p className="mb-3">
                                Token потрібен лише для того, щоб Afivio зміг підписатися на вебхуки Horoshop. Afivio <b>не зберігає</b> token.
                            </p>

                            <Accordion title="Крок 3.1 — Відкрийте термінал (Windows / macOS / online)" defaultOpen>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <div className="font-semibold text-gray-900">Windows</div>
                                        <ul className="list-disc pl-5 mt-1 space-y-1">
                                            <li>Натисніть <code>Win</code> → введіть <b>PowerShell</b> → відкрийте Windows PowerShell</li>
                                            <li>Або <code>Win</code> → введіть <b>cmd</b> → Command Prompt</li>
                                            <li>Якщо є Windows Terminal — відкрийте його і оберіть PowerShell</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">macOS</div>
                                        <ul className="list-disc pl-5 mt-1 space-y-1">
                                            <li><code>Cmd</code> + <code>Space</code> → введіть <b>Terminal</b> → Enter</li>
                                            <li>Або Applications → Utilities → Terminal</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">Онлайн-термінал (як приклад)</div>
                                        <div className="mt-1">
                                            <a className="text-blue-700 underline" href="https://www.terminaltemple.com/" target="_blank" rel="noreferrer">
                                                https://www.terminaltemple.com/
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </Accordion>

                            <Accordion title="Крок 3.2 — Виконайте запит auth (шаблон)" defaultOpen={true}>
                                <p className="text-sm mb-3">
                                    Підставте свої дані: <code>&lt;HOST&gt;</code> (домен магазину), <code>&lt;API_LOGIN&gt;</code>, <code>&lt;API_PASSWORD&gt;</code>.
                                </p>
                                <CodeBlock label="auth (template)" code={authCurlTemplate} />
                            </Accordion>

                            <Accordion title="Приклад auth з конкретними значеннями" defaultOpen>
                                <p className="text-sm mb-3">
                                    Приклад: HOST = <code>my-shop.com</code>, login = <code>api_user</code>, password = <code>pass123</code>
                                </p>
                                <CodeBlock label="auth (example)" code={authCurlExample} />
                                <div className="mt-3 text-sm text-gray-700">
                                    У відповіді знайдіть поле <b>token</b> - скопіюйте його значення.
                                </div>
                            </Accordion>
                        </Step>

                        <Step
                            n="4"
                            title="Підключіть Horoshop у Afivio (host + token)"
                            done={done.s4}
                            onToggle={() => setDone((p) => ({ ...p, s4: !p.s4 }))}
                        >
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>Вставте <b>host</b> (наприклад <code>shop.example.com</code>) та token.</li>
                                <li>Натисніть <b>Підключити</b>.</li>
                            </ol>
                            <div className="mt-4 rounded-2xl bg-green-50 border border-green-100 p-4 text-sm text-gray-800">
                                Afivio автоматично підпишеться на вебхуки: <code>order_created</code>, <code>order_paid</code>, <code>user_signup</code>.
                            </div>
                        </Step>

                        <Step
                            n="5"
                            title="Перевірте, що все працює"
                            done={done.s5}
                            onToggle={() => setDone((p) => ({ ...p, s5: !p.s5 }))}
                        >
                            <ul className="list-disc pl-5 space-y-2">
                                <li>Зробіть тестове замовлення в магазині → очікуйте подію <code>order_created</code>.</li>
                                <li>Змініть стан на оплату (або проведіть оплату) → подія <code>order_paid</code>.</li>
                                <li>Зареєструйте нового користувача → подія <code>user_signup</code>.</li>
                            </ul>
                        </Step>


                            <p className="mt-3 text-sm text-gray-700">
                                Офіційна нотатка Horoshop:{" "}
                                <a
                                    className="text-blue-700 underline"
                                    href="https://horoshop.notion.site/1b6cc289707981848292d506d0dc40ab"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    https://horoshop.notion.site/1b6cc289707981848292d506d0dc40ab
                                </a>
                            </p>
                    </section>

                    {/* Troubleshooting */}
                    <section className="mt-10 space-y-4">
                        <h2 className="text-2xl font-extrabold text-gray-900">Troubleshooting</h2>

                        <Accordion title="Який host вводити?">
                            <p className="text-sm leading-relaxed">
                                Вводьте тільки домен (host) магазину без <code>https://</code> і без шляху.
                                <br />
                                ✅ <code>shop.example.com</code>
                                <br />
                                ❌ <code>https://shop.example.com/some/path</code>
                            </p>
                        </Accordion>

                        <Accordion title="Вставив скрипт, але не бачу трекінг">
                            <ul className="list-disc pl-5 text-sm space-y-2">
                                <li>Перевірте, що скрипт доданий саме в Horoshop → Скрипти → в середині <code>&lt;head&gt;</code></li>
                                <li>Перевірте, що код не додано двічі</li>
                                <li>Очистіть кеш / відкрийте сайт в інкогніто</li>
                                <li>Переконайтесь, що після вставки ви натиснули “Зберегти” в Horoshop</li>
                            </ul>
                        </Accordion>

                        <Accordion title="Token не працює / auth повертає помилку">
                            <ul className="list-disc pl-5 text-sm space-y-2">
                                <li>Перевірте, що login/password правильні (API-користувач створений в адмінці)</li>
                                <li>Спробуйте HTTP замість HTTPS для <code>/api/auth/</code> (якщо магазин не налаштований на HTTPS)</li>
                                <li>Перевірте, що вказали правильний host</li>
                            </ul>
                        </Accordion>
                    </section>

                    {/* Footer CTA */}
                    <section className="mt-12 rounded-3xl border border-gray-200 bg-white shadow-sm p-8">
                        <h2 className="text-2xl font-extrabold text-gray-900">Готові підключати?</h2>
                        <p className="mt-2 text-gray-700">
                            Створіть акаунт, додайте компанію з Horoshop інтеграцією і пройдіть кроки в майстрі — це займе 10–20 хв.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <a
                                href="/signup"
                                className="inline-flex items-center justify-center px-5 py-3 rounded-2xl font-semibold text-white bg-blue-600 hover:bg-blue-700"
                            >
                                Почати в Afivio →
                            </a>
                            <a
                                href="mailto:contact@afivio.com?subject=Horoshop%20Integration%20Help"
                                className="inline-flex items-center justify-center px-5 py-3 rounded-2xl font-semibold text-gray-900 bg-gray-100 hover:bg-gray-200"
                            >
                                Потрібна допомога
                            </a>
                        </div>
                    </section>
                </main>
            </div>
        </>
    );
}
